<template>
  <v-dialog
    :model-value="modelValue"
    max-width="760"
    persistent
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card rounded="xl">
      <v-card-title class="d-flex align-center pa-5">
        <v-avatar color="error" variant="tonal" size="44" class="mr-3">
          <v-icon>mdi-stop-circle-outline</v-icon>
        </v-avatar>
        <div>
          <div class="text-h6 font-weight-bold">End POS Session</div>
          <div class="text-caption text-medium-emphasis">{{ sessionNumber }}</div>
        </div>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" :disabled="loading" @click="close" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-5">
        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
        <v-alert type="warning" variant="tonal" class="mb-4">
          Closing the session will prevent additional sales from being created under this session.
        </v-alert>

        <v-card variant="outlined" rounded="lg" class="mb-5">
          <v-card-title class="d-flex align-center px-4 py-3">
            <v-icon icon="mdi-chart-box-outline" class="mr-2" />
            <span class="text-subtitle-1 font-weight-bold">Session Closing Summary</span>
            <v-spacer />
            <v-btn
              icon="mdi-refresh"
              size="small"
              variant="text"
              aria-label="Refresh closing summary"
              :loading="previewLoading"
              :disabled="!sessionId || loading"
              @click="loadPreview"
            />
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-progress-linear v-if="previewLoading" indeterminate class="mb-3" />
            <v-alert v-if="previewError" type="error" variant="tonal" class="mb-3">
              {{ previewError }}
            </v-alert>

            <template v-if="preview && !previewError">
              <div class="text-caption text-medium-emphasis mb-3">
                Currency: {{ currencyCode }}
              </div>

              <div class="text-subtitle-2 font-weight-bold mb-2">Sales Activity</div>
              <div class="summary-grid mb-4">
                <div v-for="entry in saleCounts" :key="entry.key" class="summary-item">
                  <div class="text-caption text-medium-emphasis">{{ entry.label }}</div>
                  <div class="font-weight-bold">{{ formatCount(preview[entry.key]) }}</div>
                </div>
              </div>

              <div class="text-subtitle-2 font-weight-bold mb-2">Sales and Collections</div>
              <div class="summary-grid mb-4">
                <div v-for="entry in salesAmounts" :key="entry.key" class="summary-item">
                  <div class="text-caption text-medium-emphasis">{{ entry.label }}</div>
                  <div class="font-weight-bold">{{ formatMoney(preview[entry.key]) }}</div>
                </div>
              </div>

              <div class="text-subtitle-2 font-weight-bold mb-2">Cash Reconciliation</div>
              <div class="summary-grid mb-4">
                <div v-for="entry in cashAmounts" :key="entry.key" class="summary-item">
                  <div class="text-caption text-medium-emphasis">{{ entry.label }}</div>
                  <div class="font-weight-bold" :class="entry.key === 'cashDifference' && Number(preview.cashDifference) !== 0 ? 'text-warning' : ''">
                    {{ formatMoney(preview[entry.key]) }}
                  </div>
                </div>
              </div>

              <div class="text-subtitle-2 font-weight-bold mb-2">Payment Methods</div>
              <v-table density="compact" class="mb-2">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th class="text-end">Paid</th>
                    <th class="text-end">Refunded</th>
                    <th class="text-end">Net</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="method in paymentMethods" :key="method.name">
                    <td>{{ method.name }}</td>
                    <td class="text-end">{{ formatMoney(method.paidAmount) }}</td>
                    <td class="text-end">{{ formatMoney(method.refundedAmount) }}</td>
                    <td class="text-end">{{ formatMoney(method.netAmount) }}</td>
                  </tr>
                  <tr v-if="!paymentMethods.length">
                    <td colspan="4" class="text-medium-emphasis">No payment method totals recorded.</td>
                  </tr>
                </tbody>
              </v-table>
            </template>
            <div v-else-if="!previewLoading && !previewError" class="text-body-2 text-medium-emphasis">
              The session closing summary is not available yet.
            </div>
          </v-card-text>
        </v-card>

        <v-form ref="formRef" @submit.prevent="submit">
          <v-text-field
            v-model.number="closingCashAmount"
            label="Closing Cash Amount"
            prepend-inner-icon="mdi-cash-check"
            type="number"
            min="0"
            step="0.01"
            variant="outlined"
            :rules="[nonNegativeAmount]"
            :disabled="loading"
            :suffix="currencyCode"
            hint="Enter the cash physically counted in the drawer."
            persistent-hint
          />
          <v-textarea
            v-model="notes"
            label="Closing Notes"
            prepend-inner-icon="mdi-note-text-outline"
            variant="outlined"
            rows="3"
            auto-grow
            maxlength="1000"
            counter
            class="mt-3"
            :disabled="loading"
          />
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-5">
        <v-btn variant="outlined" :disabled="loading" @click="close">Cancel</v-btn>
        <v-spacer />
        <v-btn
          color="error"
          prepend-icon="mdi-stop-circle-outline"
          :loading="loading"
          :disabled="!canClose"
          @click="submit"
        >
          End Session
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import posSessionService from "@/modules/pos/services/posSessionService";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  session: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: "" }
});

const emit = defineEmits(["update:modelValue", "close-session"]);
const formRef = ref(null);
const closingCashAmount = ref(0);
const notes = ref("");
const preview = ref(null);
const previewLoading = ref(false);
const previewError = ref("");
const previewCashAmount = ref(null);
let previewRequestId = 0;
let debounceTimer = null;

const sessionId = computed(() => props.session?.id ?? props.session?.sessionId ?? null);
const sessionNumber = computed(() => preview.value?.sessionNumber ?? props.session?.sessionNumber ?? "POS Session");
const currencyCode = computed(() => preview.value?.currencyCode ?? props.session?.currencyCode ?? "");

const saleCounts = [
  { key: "totalSaleCount", label: "Total Sales" },
  { key: "completedSaleCount", label: "Completed Sales" },
  { key: "pendingSaleCount", label: "Pending Sales" },
  { key: "voidedSaleCount", label: "Voided Sales" }
];

const salesAmounts = [
  { key: "grossSalesAmount", label: "Gross Sales" },
  { key: "discountAmount", label: "Discounts" },
  { key: "taxAmount", label: "Tax" },
  { key: "tipAmount", label: "Tips" },
  { key: "adjustmentAmount", label: "Adjustments" },
  { key: "invoiceTotalAmount", label: "Invoice Total" },
  { key: "paidAmount", label: "Payments Received" },
  { key: "refundedAmount", label: "Refunded" },
  { key: "netCollectedAmount", label: "Net Collected" }
];

const cashAmounts = [
  { key: "openingCashAmount", label: "Opening Cash" },
  { key: "cashSalesNetAmount", label: "Net Cash Sales" },
  { key: "expectedCashAmount", label: "Expected Cash" },
  { key: "closingCashAmount", label: "Counted Closing Cash" },
  { key: "cashDifference", label: "Cash Difference" }
];

const paymentMethods = computed(() => {
  const totals = preview.value?.paymentMethodTotals;
  if (!totals || typeof totals !== "object" || Array.isArray(totals)) return [];
  return Object.entries(totals).map(([name, amounts]) => ({
    name,
    paidAmount: amounts?.paidAmount,
    refundedAmount: amounts?.refundedAmount,
    netAmount: amounts?.netAmount
  }));
});

function formatMoney(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value));
}

function formatCount(value) {
  return Number.isInteger(Number(value)) && value !== null && value !== undefined
    ? new Intl.NumberFormat().format(Number(value))
    : "—";
}

const nonNegativeAmount = value => {
  if (value === null || value === undefined || value === "") {
    return "Closing cash amount is required.";
  }
  const amount = Number(value);
  return (Number.isFinite(amount) && amount >= 0) || "Closing cash cannot be negative.";
};

const canClose = computed(() =>
  Boolean(sessionId.value) &&
  !props.loading &&
  !previewLoading.value &&
  !previewError.value &&
  Boolean(preview.value) &&
  previewCashAmount.value === Number(closingCashAmount.value) &&
  preview.value.sessionId?.toLowerCase() === String(sessionId.value).toLowerCase() &&
  cashAmounts.every(entry => preview.value[entry.key] !== undefined && preview.value[entry.key] !== null)
);

async function loadPreview() {
  if (!props.modelValue || !sessionId.value) return;
  clearTimeout(debounceTimer);
  const amount = Number(closingCashAmount.value);
  const requestId = ++previewRequestId;
  if (closingCashAmount.value === "" || closingCashAmount.value === null ||
      !Number.isFinite(amount) || amount < 0) {
    preview.value = null;
    previewCashAmount.value = null;
    previewLoading.value = false;
    previewError.value = "Enter a valid non-negative closing cash amount.";
    return;
  }

  previewLoading.value = true;
  previewError.value = "";
  preview.value = null;
  previewCashAmount.value = null;

  try {
    const data = await posSessionService.previewClose(sessionId.value, amount);
    if (requestId !== previewRequestId || !props.modelValue) return;
    if (!data || typeof data !== "object" ||
        String(data.sessionId ?? "").toLowerCase() !== String(sessionId.value).toLowerCase()) {
      throw new Error("The closing summary does not match the selected POS session.");
    }
    preview.value = data;
    previewCashAmount.value = amount;
  } catch (error) {
    if (requestId !== previewRequestId || !props.modelValue) return;
    previewError.value = error?.response?.data?.message || error?.message ||
      "Unable to load the session closing summary.";
  } finally {
    if (requestId === previewRequestId) previewLoading.value = false;
  }
}

async function submit() {
  if (props.loading || previewLoading.value) return;
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;
  if (!sessionId.value) return;
  if (!canClose.value) {
    await loadPreview();
    return;
  }

  emit("close-session", {
    sessionId: sessionId.value,
    closingCashAmount: Number(closingCashAmount.value),
    notes: notes.value?.trim() || null
  });
}

function close() {
  if (props.loading) return;
  emit("update:modelValue", false);
}

function reset() {
  closingCashAmount.value = 0;
  notes.value = "";
  preview.value = null;
  previewError.value = "";
  previewCashAmount.value = null;
  previewLoading.value = false;
  formRef.value?.resetValidation();
}

watch(() => [props.modelValue, sessionId.value], ([opened]) => {
  clearTimeout(debounceTimer);
  ++previewRequestId;
  if (opened) {
    reset();
    loadPreview();
  } else {
    preview.value = null;
    previewLoading.value = false;
  }
}, { immediate: true });

watch(closingCashAmount, () => {
  if (!props.modelValue) return;
  clearTimeout(debounceTimer);
  ++previewRequestId;
  preview.value = null;
  previewCashAmount.value = null;
  previewLoading.value = false;
  debounceTimer = setTimeout(loadPreview, 350);
});

onBeforeUnmount(() => {
  clearTimeout(debounceTimer);
  ++previewRequestId;
});
</script>

<style scoped>
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.summary-item {
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  min-width: 0;
}
@media (max-width: 500px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
